"use client";

import { Button } from "@/components/ui/button";
import { CheckCircle, AlertCircle, Loader2, Plus } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export interface OAuthInboxInfo {
  id: string;
  email: string;
  hasScope: boolean;
}

export interface OAuthScopeConnectionStatusProps {
  /** Loading state */
  isLoading?: boolean;
  /** List of inboxes with their scope status */
  inboxes?: OAuthInboxInfo[];
  /** Callback when user clicks connect on an inbox */
  onConnect: (inboxId: string) => void;
  /** Icon to show for inboxes without the scope */
  icon: LucideIcon;
  /** Text shown while loading */
  loadingText?: string;
  /** Text for connect button */
  connectButtonText?: string;
  /** Text shown when inbox has the scope */
  connectedText?: string;
  /** Summary text when all inboxes have the scope */
  allConnectedText?: string;
  /** Title for empty state (no inboxes) */
  emptyStateTitle?: string;
  /** Description for empty state (no inboxes) */
  emptyStateDescription?: string;
}

/**
 * Generic component for displaying OAuth scope connection status across inboxes.
 * Used for incremental OAuth authorization (calendar, gmail.modify, etc.)
 */
export function OAuthScopeConnectionStatus({
  isLoading,
  inboxes = [],
  onConnect,
  icon: Icon,
  loadingText = "Checking permissions...",
  connectButtonText = "Grant Access",
  connectedText = "Ready",
  allConnectedText = "All inboxes have the required permissions",
  emptyStateTitle = "No email account connected",
  emptyStateDescription = "Connect an email account first in the \"Inboxes\" tab",
}: OAuthScopeConnectionStatusProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
        <span className="text-sm text-gray-600">{loadingText}</span>
      </div>
    );
  }

  // No inboxes at all
  if (inboxes.length === 0) {
    return (
      <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-amber-800">{emptyStateTitle}</p>
          <p className="text-xs text-amber-600">{emptyStateDescription}</p>
        </div>
      </div>
    );
  }

  const connectedInboxes = inboxes.filter((i) => i.hasScope);
  const notConnectedInboxes = inboxes.filter((i) => !i.hasScope);

  return (
    <div className="space-y-2">
      {/* Connected inboxes */}
      {connectedInboxes.map((inbox) => (
        <div
          key={inbox.id}
          className="flex items-center justify-between p-2.5 bg-green-50 border border-green-200 rounded-lg"
        >
          <div className="flex items-center gap-2 min-w-0">
            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
            <span className="text-sm text-green-800 truncate">{inbox.email}</span>
          </div>
          <span className="text-xs text-green-600 flex-shrink-0">{connectedText}</span>
        </div>
      ))}

      {/* Not connected inboxes */}
      {notConnectedInboxes.map((inbox) => (
        <div
          key={inbox.id}
          className="flex items-center justify-between p-2.5 bg-gray-50 border border-gray-200 rounded-lg"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="text-sm text-gray-600 truncate">{inbox.email}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onConnect(inbox.id)}
            className="h-7 px-2 text-xs gap-1 flex-shrink-0"
          >
            <Plus className="w-3 h-3" />
            {connectButtonText}
          </Button>
        </div>
      ))}

      {/* Summary if all connected */}
      {notConnectedInboxes.length === 0 && connectedInboxes.length > 0 && (
        <p className="text-xs text-green-600 mt-1">{allConnectedText}</p>
      )}
    </div>
  );
}
